// Marknadssidan på "/" för besökare som inte är inloggade. Ren HTML, CSS och SVG, inga externa resurser.
import { Link } from "@tanstack/react-router";
import { type CSSProperties, type ReactNode, useCallback, useState } from "react";
import "../styles/landing.css";
import { Icon, type IconName } from "../components/Icon.tsx";
import { usePageTitle } from "../lib/title.ts";

export const LEGAL_LINKS: { to: string; label: string }[] = [
  { to: "/integritet", label: "Integritetspolicy" },
  { to: "/villkor", label: "Användarvillkor" },
  { to: "/pub", label: "Personuppgiftsbiträdesavtal" },
  { to: "/cookies", label: "Cookies" },
  { to: "/tillganglighet", label: "Tillgänglighet" },
];

export function SiteHeader({ children }: { children?: ReactNode }) {
  return (
    <header className="site-header">
      <div className="site-wrap site-header-row">
        <Link to="/" className="site-brand">
          <img src="/favicon.svg" alt="" width="28" height="28" />
          <span>TestViora</span>
        </Link>
        {children}
        <div className="site-header-actions">
          <Link to="/logga-in" className="btn btn-ghost">
            Logga in
          </Link>
          <Link to="/skapa-konto" className="btn btn-primary site-header-cta">
            Skapa konto
          </Link>
        </div>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-wrap site-footer-grid">
        <div className="stack-s">
          <span className="site-brand site-brand-footer">
            <img src="/favicon.svg" alt="" width="24" height="24" />
            <span>TestViora</span>
          </span>
          <p className="small">Behov, krav och test på ett ställe. Ni jobbar ihop i realtid.</p>
          <p className="small">
            Meditude AB, <a href="mailto:info@meditude.se">info@meditude.se</a>
          </p>
        </div>
        <nav aria-label="Produkten">
          <h2 className="site-footer-h">Produkten</h2>
          <ul>
            <li><a href="/#resan">Resan</a></li>
            <li><a href="/#utvecklare">Utvecklare och AI</a></li>
            <li><a href="/#offentlig-sektor">Offentlig sektor</a></li>
            <li><a href="/#priser">Priser</a></li>
            <li><a href="/#fragor">Vanliga frågor</a></li>
          </ul>
        </nav>
        <nav aria-label="Juridik och tillgänglighet">
          <h2 className="site-footer-h">Juridik</h2>
          <ul>
            {LEGAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <nav aria-label="Konto">
          <h2 className="site-footer-h">Konto</h2>
          <ul>
            <li><Link to="/skapa-konto">Skapa konto</Link></li>
            <li><Link to="/logga-in">Logga in</Link></li>
          </ul>
        </nav>
      </div>
      <div className="site-wrap site-footer-base small">
        <span>© 2026 Meditude AB</span>
        <span>Drift i EU (Finland). Inga spårare. Bara en kaka som behövs för att logga in.</span>
      </div>
    </footer>
  );
}

/** Den levande kedjan: ett behov bryts ner till krav, testfallen blir gröna och täckningen fylls. */
/** Status i utdraget: ringens form och ikon, samma som i tankekartan (ringOf i pages/MindMap.tsx). */
const RING: Record<string, { shape: string; icon: IconName; text: string }> = {
  godkand: { shape: "hel", icon: "check", text: "Verifierad" },
  underkand: { shape: "bruten", icon: "x", text: "Underkänd" },
  saknar_test: { shape: "streckad", icon: "alert", text: "Saknar test" },
  delvis: { shape: "halv", icon: "circle", text: "Delvis" },
};

function Ring({ status }: { status: string }) {
  const r = RING[status] ?? RING.delvis!;
  return (
    <span className={`mm-ring mm-ring-${r.shape}`}>
      <svg className="mm-ring-bg" viewBox="0 0 28 28" width="28" height="28" focusable="false" aria-hidden="true">
        <circle cx="14" cy="14" r="11" />
      </svg>
      <Icon name={r.icon} size={12} />
    </span>
  );
}

interface ShotNode {
  key: string;
  kind: string;
  cls: string;
  title: string;
  status: string;
  x: number;
  y: number;
  w: number;
  h: number;
  center?: boolean;
  testStatus?: string;
}
interface ShotLayout {
  width: number;
  height: number;
  nodes: ShotNode[];
  edges: { d: string; gap: boolean }[];
}

/** Posterna i utdraget. Placeringen skiljer sig mellan kartan på bred skärm och den lodräta kartan på mobil. */
const ITEMS = {
  cap: { key: "FÖR-2", kind: "Förmåga", cls: "capability", title: "Bokning och ombokning", status: "delvis" },
  need: { key: "BEH-4", kind: "Behov", cls: "need", title: "Som medborgare behöver jag boka tid för rådgivning för att slippa ringa", status: "delvis", center: true },
  r12: { key: "KRAV-12", kind: "Krav", cls: "requirement", title: "Systemet ska visa lediga tider inom 2 sekunder.", status: "godkand" },
  r13: { key: "KRAV-13", kind: "Krav", cls: "requirement", title: "När en tid bokas ska systemet skicka en bekräftelse inom 1 minut.", status: "underkand" },
  r14: { key: "KRAV-14", kind: "Krav", cls: "requirement", title: "Användaren bör kunna avboka fram till dagen före besöket.", status: "saknar_test" },
  t21: { key: "TF-21", kind: "Testfall", cls: "test", title: "Visa lediga tider för nästa vecka", status: "godkand", testStatus: "Godkänd" },
  t22: { key: "TF-22", kind: "Testfall", cls: "test", title: "Boka en tid och få bekräftelse", status: "underkand", testStatus: "Underkänd" },
};
type Item = (typeof ITEMS)[keyof typeof ITEMS];
const at = (item: Item, x: number, y: number, w: number, h: number): ShotNode => ({ ...item, x, y, w, h });
/** En linje är streckad när posten den leder till är en lucka, som i tjänsten. */
const gapOf = (n: ShotNode) => n.status !== "godkand";

/** Kedjan på bred skärm: förmågan till vänster, behovet i mitten, kraven och testfallen till höger. */
function wideLayout(): ShotLayout {
  const W = 260;
  const H = 112;
  const col = [24, 356, 688, 1020] as const;
  const nodes = [
    at(ITEMS.cap, col[0], 168, W, H),
    at(ITEMS.need, col[1], 168, W, H),
    at(ITEMS.r12, col[2], 28, W, H),
    at(ITEMS.r13, col[2], 168, W, H),
    at(ITEMS.r14, col[2], 308, W, H),
    at(ITEMS.t21, col[3], 28, W, H),
    at(ITEMS.t22, col[3], 168, W, H),
  ];
  const [cap, need, r12, r13, r14, t21, t22] = nodes as [ShotNode, ShotNode, ShotNode, ShotNode, ShotNode, ShotNode, ShotNode];
  const link = (a: ShotNode, b: ShotNode) => {
    const x1 = a.x + a.w;
    const y1 = a.y + a.h / 2;
    const x2 = b.x;
    const y2 = b.y + b.h / 2;
    const mx = (x1 + x2) / 2;
    return { d: `M${x1} ${y1} C${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`, gap: gapOf(b) };
  };
  return { width: 1304, height: 448, nodes, edges: [link(cap, need), link(need, r12), link(need, r13), link(need, r14), link(r12, t21), link(r13, t22)] };
}

/**
 * Kartan på mobil, lodrät som när kartan vänds i tjänsten: förmågan överst, behovet under, kraven på en rad och testfallen
 * under sina krav. Nivåerna går uppifrån och ned och linjerna går från nederkanten till överkanten.
 */
function narrowLayout(): ShotLayout {
  const W = 156;
  const gap = 12;
  const level = 48;
  const width = 3 * W + 2 * gap;
  const col = [0, W + gap, 2 * (W + gap)] as const;
  const wideW = 2 * W + gap;
  const mid = (width - wideW) / 2;
  const yCap = 0;
  const yNeed = yCap + 84 + level;
  const yReq = yNeed + 136 + level;
  const yTest = yReq + 184 + level;
  const nodes = [
    at(ITEMS.cap, mid, yCap, wideW, 84),
    at(ITEMS.need, mid, yNeed, wideW, 136),
    at(ITEMS.r12, col[0], yReq, W, 184),
    at(ITEMS.r13, col[1], yReq, W, 184),
    at(ITEMS.r14, col[2], yReq, W, 184),
    at(ITEMS.t21, col[0], yTest, W, 124),
    at(ITEMS.t22, col[1], yTest, W, 124),
  ];
  const [cap, need, r12, r13, r14, t21, t22] = nodes as [ShotNode, ShotNode, ShotNode, ShotNode, ShotNode, ShotNode, ShotNode];
  const link = (a: ShotNode, b: ShotNode) => {
    const x1 = a.x + a.w / 2;
    const y1 = a.y + a.h;
    const x2 = b.x + b.w / 2;
    const y2 = b.y;
    const my = (y1 + y2) / 2;
    return { d: `M${x1} ${y1} C${x1} ${my} ${x2} ${my} ${x2} ${y2}`, gap: gapOf(b) };
  };
  return { width, height: yTest + 124, nodes, edges: [link(cap, need), link(need, r12), link(need, r13), link(need, r14), link(r12, t21), link(r13, t22)] };
}

const WIDE = wideLayout();
const NARROW = narrowLayout();

/** Skalar kartan efter ytans bredd, som Anpassa i tankekartan. */
function useFit(width: number): [(el: HTMLDivElement | null) => (() => void) | undefined, number] {
  const [scale, setScale] = useState(0.8);
  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return;
      const set = () => setScale(Math.min(1, (el.clientWidth - 32) / width));
      set();
      const ro = new ResizeObserver(set);
      ro.observe(el);
      return () => ro.disconnect();
    },
    [width],
  );
  return [ref, scale];
}

/**
 * Utdrag ur tankekartan, byggt med samma klasser som i tjänsten: kedjan runt ett behov. Det är en bild av produkten, så den är dold för skärmläsare och beskrivs i bildtexten.
 */
function ShotMap({ layout, className }: { layout: ShotLayout; className: string }) {
  const [fitRef, scale] = useFit(layout.width);
  return (
    <div ref={fitRef} className={`shot-stage ${className}`} style={{ height: layout.height * scale + 32 }}>
      <div className="shot-inner" style={{ width: layout.width, height: layout.height, transform: `translate(16px, 16px) scale(${scale})` }}>
        <svg className="mm-edges" width={layout.width} height={layout.height} focusable="false" aria-hidden="true">
          {layout.edges.map((e) => (
            <path key={e.d} d={e.d} className={e.gap ? "mm-edge mm-edge-gap" : "mm-edge"} />
          ))}
        </svg>
        {layout.nodes.map((n) => (
          <div key={n.key} className={`mm-node mm-${n.cls} ${n.center ? "mm-center" : ""}`} style={{ width: n.w, transform: `translate(${n.x}px, ${n.y}px)`, "--mm-h": `${n.h}px` } as CSSProperties}>
            <Ring status={n.status} />
            <span className="mm-text">
              <span className="mm-key">
                <span className="mono">{n.key}</span> <span className="mm-kind">{n.kind}</span>
              </span>
              <span className="mm-title">{n.title}</span>
              <span className="mm-status">{n.testStatus ?? RING[n.status]?.text}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Utdrag ur tankekartan, byggt med samma klasser som i tjänsten: kedjan runt ett behov, liggande på bred skärm och
 * lodrät på mobil som när kartan vänds. Det är en bild av produkten, så den är dold för skärmläsare och beskrivs i bildtexten.
 */
function ProductShot() {
  return (
    <figure className="shot" aria-labelledby="shot-caption">
      <div className="shot-frame" aria-hidden="true">
        <div className="shot-bar">
          <span className="shot-search shot-hide-small">Sök förmåga, behov, krav eller testfall</span>
          <span className="shot-tool shot-tool-ghost shot-hide-small">Filter</span>
          <span className="shot-tool shot-hide-small">
            <Icon name="minus" size={14} /> 100 % <Icon name="plus" size={14} />
          </span>
          <span className="shot-tool shot-hide-small">Anpassa</span>
          <span className="shot-tool shot-hide-small">
            <Icon name="maximize" size={14} /> Helskärm
          </span>
        </div>
        <p className="shot-summary">BEH-4 Som medborgare behöver jag boka tid för rådgivning. Delvis. 3 krav: 1 verifierat, 1 underkänt, 1 saknar test.</p>
        <ShotMap layout={WIDE} className="shot-wide" />
        <ShotMap layout={NARROW} className="shot-narrow" />
      </div>
      <figcaption id="shot-caption">
        Tankekartan i TestViora: ett behov med förmågan det hör till och kraven och testfallen som verifierar det. Ringen och texten visar status, och en
        streckad linje visar en lucka.
      </figcaption>
    </figure>
  );
}

const STEPS = [
  { n: "Fånga", t: "Behov från alla håll", d: "Ta in behov via ett formulär som inte kräver konto. Eller hämta dem från Excel. Eller håll en workshop där alla röstar från mobilen. Varje behov blir ett eget objekt med värde och röster." },
  { n: "Validera", t: "Rätt sak innan den byggs", d: "Pröva behovet mot en checklista. Ge det en prioritet: Måste, Viktigt, Önskvärt eller Inte nu. Ge det ett värde från 1 till 5. Det som inte håller avvisas, med ett skäl som alla ser." },
  { n: "Bryta ner", t: "Från behov till krav", d: "Bryt ner behovet till krav, med AI eller för hand. Kraven granskas medan du skriver. Det sker enligt vedertagna regler för kravtext. Vaga ord fångas direkt." },
  { n: "Prioritera", t: "Ska och bör", d: "Sortera, filtrera och prioritera i ett rutnät. Det känns som ett kalkylark, men har spårbarhet och revisionslogg." },
  { n: "Testa", t: "Testfall med ett klick", d: "Skapa testfall ur acceptanskriterierna. Kör testen ihop i realtid, med kortkommandon. Är ett steg underkänt? Skapa en avvikelse direkt från det." },
  { n: "Spåra", t: "Hela kedjan, alltid aktuell", d: "Täckningsmatris, rapporter och baslinjer visar vilka behov som är levererade. De visar också med vilka bevis. Automatiska tester från CI räknas med." },
];

type Cell = "ja" | "delvis" | "nej";
const COMPARE: { what: string; cells: [Cell, Cell, Cell, Cell] }[] = [
  { what: "Behov som eget objekt, med röster och validering", cells: ["nej", "delvis", "nej", "ja"] },
  { what: "Kontroll av kravtext medan du skriver", cells: ["nej", "delvis", "nej", "ja"] },
  { what: "Testfall direkt ur acceptanskriterierna", cells: ["nej", "nej", "delvis", "ja"] },
  { what: "Flera testare i samma körning, i realtid", cells: ["delvis", "nej", "delvis", "ja"] },
  { what: "Tester från CI räknas i täckningen av krav", cells: ["nej", "delvis", "ja", "ja"] },
  { what: "Spårbarhet från behov till resultat i en vy", cells: ["nej", "delvis", "delvis", "ja"] },
  { what: "Testare, granskare och intressenter gratis", cells: ["ja", "nej", "nej", "ja"] },
  { what: "Svenska, skall och bör, drift i EU", cells: ["delvis", "delvis", "delvis", "ja"] },
];
const CELL_TEXT: Record<Cell, string> = { ja: "Ja", delvis: "Delvis", nej: "Nej" };
const CELL_ICON: Record<Cell, IconName> = { ja: "check", delvis: "minus", nej: "x" };

const FAQ: { q: string; a: ReactNode }[] = [
  {
    q: "Kan vi flytta över våra Excel-listor?",
    a: "Ja. Importen gissar vilka kolumner som är rubrik, beskrivning, prioritet och status. Du kan ändra innan något sparas. Du kan också exportera tillbaka till Excel eller CSV när som helst.",
  },
  {
    q: "Vad räknas som redaktör?",
    a: "Ägare, administratörer och redaktörer. Alltså de som skapar och ändrar behov, krav och testfall. Testare, granskare och intressenter kostar inget. Det gäller hur många de än är.",
  },
  {
    q: "Var sparas våra uppgifter?",
    a: (
      <>
        Hos Hetzner i Finland, inom EU. Varje organisation hålls skild från de andra med säkerhet på radnivå i databasen. Läs mer i <Link to="/integritet">integritetspolicyn</Link>.
      </>
    ),
  },
  {
    q: "Används vår data för att träna AI?",
    a: "Nej. AI-funktionen använder Anthropic som underbiträde. Kunddata används inte för träning. AI ger bara förslag. Du säger själv ja eller nej till dem. Ni kan stänga av funktionen helt för hela organisationen.",
  },
  {
    q: "Fungerar det för offentlig upphandling?",
    a: "Ja. Krav märks som skall eller bör. De kan låsas i baslinjer och exporteras som underlag till upphandling. Revisionsloggen visar vem som ändrade vad och när.",
  },
  {
    q: "Hur kopplar vi våra automattester?",
    a: "Tagga testet med kravets nyckel, till exempel @KRAV-12. Ladda sedan upp rapporten från JUnit eller Playwright. Det gör du med vår CLI, GitHub Action eller GitLab-mall. Resultatet räknas direkt i täckningen.",
  },
  {
    q: "Kan vi koppla till Jira eller Azure DevOps?",
    a: "Ja. Krav skickas ut som ärenden. Status hämtas tillbaka. Det finns också aviseringar till Slack och Teams. Och det finns signerade webhooks och ett öppet REST-API.",
  },
  {
    q: "Är TestViora tillgängligt?",
    a: (
      <>
        Målet är WCAG 2.2 nivå AA. Allt går att använda med tangentbord. Vi testar med axe före varje publicering. Kända brister står i{" "}
        <Link to="/tillganglighet">tillgänglighetsredogörelsen</Link>.
      </>
    ),
  },
];

export function LandingPage() {
  usePageTitle("Behov, krav och test i samma kedja");
  return (
    <div className="landing">
      <a className="skip-link" href="#main">
        Hoppa till innehållet
      </a>
      <SiteHeader>
        <nav className="site-nav" aria-label="Sidans delar">
          <ul>
            <li><a href="#resan">Resan</a></li>
            <li><a href="#varfor">Varför TestViora</a></li>
            <li><a href="#utvecklare">Utvecklare</a></li>
            <li><a href="#offentlig-sektor">Offentlig sektor</a></li>
            <li><a href="#priser">Priser</a></li>
          </ul>
        </nav>
      </SiteHeader>

      <main id="main" tabIndex={-1}>
        <section className="hero" aria-labelledby="hero-title">
          <div className="site-wrap">
            <div className="hero-copy">
              <h1 id="hero-title">Behov, krav och test i samma kedja</h1>
              <div className="stack">
                <p className="hero-lead">
                  TestViora håller ihop det verksamheten behöver med krav och test. Kraven beskriver behovet. Testen visar att det fungerar. Alla jobbar i samma
                  tjänst. Du ser alltid vad som är klart och vad som saknas.
                </p>
                <div className="hero-actions">
                  <Link to="/skapa-konto" className="btn btn-primary lbtn">
                    Skapa konto
                  </Link>
                  <Link to="/logga-in" className="btn btn-ghost lbtn">
                    Logga in
                  </Link>
                </div>
                <p className="hero-note small">Gratis för tre redaktörer. Testare och intressenter är alltid gratis.</p>
              </div>
            </div>
            <ProductShot />
          </div>
        </section>

        <section className="band" aria-labelledby="problem-title">
          <div className="site-wrap">
            <h2 id="problem-title" className="lp-title">
              Därför räcker inte kalkylarket
            </h2>
            <ul className="points">
              <li>
                <h3>Kalkylark som ingen litar på</h3>
                <p>Kravlistan finns i fem versioner. Någon har ändrat en rad, men ingen vet vem eller varför. Den som ska testa får en kopia som redan är gammal.</p>
              </li>
              <li>
                <h3>Verktyg som inte pratar med varandra</h3>
                <p>Behoven ligger i mejl. Kraven finns i ett verktyg och testen i ett annat. Varje möte om läget börjar likadant. Någon klistrar ihop en rapport för hand.</p>
              </li>
              <li>
                <h3>Spårbarhet i efterhand</h3>
                <p>Beställaren frågar om ett behov är levererat och testat. Svaret blir ”vi återkommer”. Bevisen finns, men de hänger inte ihop.</p>
              </li>
            </ul>
          </div>
        </section>

        <section id="resan" className="band band-alt" aria-labelledby="resan-title">
          <div className="site-wrap">
            <h2 id="resan-title" className="lp-title">
              Så fungerar det
            </h2>
            <p className="section-lead">Allt hänger ihop, så du kan börja var du vill och ändå få hela kedjan.</p>
            <ol className="points points-steps">
              {STEPS.map((s, i) => (
                <li key={s.n}>
                  <p className="lstep-name">
                    {i + 1}. {s.n}
                  </p>
                  <h3>{s.t}</h3>
                  <p>{s.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="varfor" className="band" aria-labelledby="varfor-title">
          <div className="site-wrap">
            <h2 id="varfor-title" className="lp-title">
              Varför TestViora
            </h2>
            <p className="section-lead">
              Kalkylark är flexibla. Verktyg för krav är bra på krav. Verktyg för test är bra på test. TestViora är byggt för hela vägen mellan dem.
            </p>
            {/* biome-ignore lint/a11y/noNoninteractiveTabindex: tabellen rullar i sidled på små skärmar och måste kunna nås med tangentbord */}
            <section className="table-scroll" aria-labelledby="compare-caption" tabIndex={0}>
              <table className="compare">
                <caption id="compare-caption">Jämförelse mellan kalkylark, kravverktyg, testverktyg och TestViora</caption>
                <thead>
                  <tr>
                    <th scope="col">Förmåga</th>
                    <th scope="col">Kalkylark</th>
                    <th scope="col">Kravverktyg</th>
                    <th scope="col">Testverktyg</th>
                    <th scope="col" className="compare-us">TestViora</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((r) => (
                    <tr key={r.what}>
                      <th scope="row">{r.what}</th>
                      {r.cells.map((c, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: fasta kolumner
                        <td key={i} className={`cell-${c} ${i === 3 ? "compare-us" : ""}`}>
                          <Icon name={CELL_ICON[c]} size={14} /> {CELL_TEXT[c]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <p className="small muted compare-note">Jämförelsen gäller vanliga verktyg av varje slag. Ett visst verktyg kan ha mer eller mindre.</p>
          </div>
        </section>

        <section id="utvecklare" className="band band-dark" aria-labelledby="dev-title">
          <div className="site-wrap dev-grid">
            <div>
              <h2 id="dev-title" className="lp-title">
                Kraven där koden finns
              </h2>
              <ul className="dev-list">
                <li>
                  <h3>CI som räknas</h3>
                  <p>Tagga testet med @KRAV-12. Ladda upp JUnit och Playwright från GitHub Actions eller GitLab. Täckningen räknas om direkt.</p>
                </li>
                <li>
                  <h3>CLI som grind</h3>
                  <p>
                    <code>testviora coverage</code> slutar med fel om ett ska-krav är underkänt. <code>testviora gaps</code> listar krav som saknar test.
                  </p>
                </li>
                <li>
                  <h3>VS Code</h3>
                  <p>Håll musen över KRAV-12 i koden. Då ser du kravet, status och senaste resultat. Trädet med krav och luckorna finns i panelen vid sidan.</p>
                </li>
                <li>
                  <h3>MCP för AI-agenter</h3>
                  <p>Din AI-assistent kan söka krav och granska hur de är skrivna. Den kan skapa testfall ur kriterierna och rapportera resultat. Den får göra samma saker som en API-token.</p>
                </li>
              </ul>
            </div>
            <div className="code-stack">
              <div className="code-card">
                <p className="code-title">tests/bokning.spec.ts</p>
                <pre>
                  <code>
                    <span className="c-k">test</span>(<span className="c-s">"visar lediga tider @KRAV-12"</span>, <span className="c-k">async</span> ({"{ page }"}) =&gt; {"{"}
                    {"\n  "}
                    <span className="c-k">await</span> page.goto(<span className="c-s">"/boka"</span>);
                    {"\n  "}
                    <span className="c-k">await</span> expect(page.getByRole(<span className="c-s">"list"</span>)).toBeVisible();
                    {"\n"}
                    {"});"}
                  </code>
                </pre>
              </div>
              <div className="code-card">
                <p className="code-title">CI</p>
                <pre>
                  <code>
                    <span className="c-p">$</span> testviora upload results/junit.xml --name <span className="c-s">"Nattbygge"</span>
                    {"\n"}
                    <span className="c-o">TestViora: 48 test, 47 godkända, 1 underkända, 0 överhoppade. 21 krav uppdaterade.</span>
                    {"\n"}
                    <span className="c-p">$</span> testviora coverage
                    {"\n"}
                    <span className="c-o">Verifierat: 94 %  Krav: 22  Utan test: 1  Underkända: 1</span>
                  </code>
                </pre>
              </div>
              <div className="code-card">
                <p className="code-title">MCP</p>
                <pre>
                  <code>
                    <span className="c-c">{"// Verktyg som AI-agenten får"}</span>
                    {"\n"}sok, lista_krav, granska_kravtext,{"\n"}skapa_testfall_ur_kriterier, tackning,{"\n"}fyll_luckor, rapportera_resultat
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </section>

        <section id="offentlig-sektor" className="band" aria-labelledby="pub-title">
          <div className="site-wrap">
            <h2 id="pub-title" className="lp-title">
              Byggt för offentlig sektor
            </h2>
            <p className="section-lead">Kommuner, regioner och myndigheter ställer krav. Kraven ska hålla när någon granskar dem. Det är TestViora gjort för.</p>
            <ul className="points points-4">
              <li>
                <h3>Ska och bör</h3>
                <p>Varje krav är ska eller bör. Täckningen visar ska-kraven för sig. Du ser direkt om ett ska-krav saknar test.</p>
              </li>
              <li>
                <h3>Upphandling och baslinjer</h3>
                <p>Lås en version av kraven i en baslinje. Jämför med tidigare. Ta fram underlag till upphandlingen. Revisionsloggen kan inte ändras i efterhand.</p>
              </li>
              <li>
                <h3>WCAG 2.2 och EN 301 549</h3>
                <p>Ställ krav på tillgänglighet enligt standarden. Följ upp dem med test. TestViora har själv målet WCAG 2.2 nivå AA.</p>
              </li>
              <li>
                <h3>Drift i EU</h3>
                <p>All data sparas i Finland. Personuppgiftsbiträdesavtal ingår. Ni kan stänga av AI per organisation. Ingen data används för träning.</p>
              </li>
            </ul>
          </div>
        </section>

        <section id="priser" className="band band-alt" aria-labelledby="pris-title">
          <div className="site-wrap">
            <h2 id="pris-title" className="lp-title">
              Priser
            </h2>
            <p className="section-lead">Du betalar bara för redaktörer. Testare, granskare och intressenter är alltid gratis.</p>
            <ul className="plans">
              <li className="plan">
                <h3>Gratis</h3>
                <p className="plan-price">0 kr</p>
                <ul className="plan-list">
                  <li>3 redaktörer</li>
                  <li>2 projekt</li>
                  <li>Hur många testare och intressenter som helst</li>
                  <li>AI-förslag, 30 per dag</li>
                  <li>Import och export till Excel</li>
                </ul>
                <Link to="/skapa-konto" className="btn btn-primary">
                  Kom igång gratis
                </Link>
              </li>
              <li className="plan plan-featured">
                <h3>Team</h3>
                <p className="plan-price">Pris per redaktör och månad</p>
                <ul className="plan-list">
                  <li>Upp till 50 redaktörer</li>
                  <li>Upp till 100 projekt</li>
                  <li>AI-förslag, 1 000 per dag</li>
                  <li>Jira, Azure DevOps, Slack och Teams</li>
                  <li>CI, CLI, MCP och VS Code</li>
                </ul>
                <a href="mailto:info@meditude.se?subject=TestViora%20Team" className="btn">
                  Kontakta oss
                </a>
              </li>
              <li className="plan">
                <h3>Business</h3>
                <p className="plan-price">Pris per redaktör och månad</p>
                <ul className="plan-list">
                  <li>Så många redaktörer och projekt ni behöver</li>
                  <li>AI-förslag, 5 000 per dag</li>
                  <li>Baslinjer, rapporter och upphandling</li>
                  <li>Personlig hjälp vid start</li>
                  <li>Faktura och avtal för offentlig sektor</li>
                </ul>
                <a href="mailto:info@meditude.se?subject=TestViora%20Business" className="btn">
                  Kontakta oss
                </a>
              </li>
            </ul>
          </div>
        </section>

        <section id="fragor" className="band" aria-labelledby="faq-title">
          <div className="site-wrap site-narrow">
            <h2 id="faq-title" className="lp-title">
              Vanliga frågor
            </h2>
            <div className="faq">
              {FAQ.map((f) => (
                <details key={f.q}>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="band cta-band" aria-labelledby="cta-title">
          <div className="site-wrap">
            <div className="cta">
              <h2 id="cta-title">Lägg Excel-listan åt sidan i dag</h2>
              <p>Skapa ett konto på en minut. Ett projekt med exempel visar hela kedjan direkt.</p>
              <div className="hero-actions">
                <Link to="/skapa-konto" className="btn btn-primary lbtn">
                  Skapa konto
                </Link>
                <Link to="/logga-in" className="btn btn-ghost lbtn">
                  Logga in
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
